import posthog from "posthog-js";

// PostHog configuration utility
class PostHogAnalytics {
  constructor() {
    this.isInitialized = false;
    this.isEnabled = false;
  }

  init() {
    // Only initialize on client side
    if (typeof window === "undefined") return;

    const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
    const apiHost =
      import.meta.env.VITE_POSTHOG_API_HOST || "https://edge.codedesign.ai/y";

    // Don't initialize in development or test environments unless explicitly enabled
    const isDev =
      import.meta.env.MODE === "development" ||
      window.location.host.includes("127.0.0.1") ||
      window.location.host.includes("localhost");

    const forceEnable = import.meta.env.VITE_POSTHOG_FORCE_ENABLE === "true";

    if (!apiKey) {
      console.warn("PostHog API key not found. PostHog analytics disabled.");
      return;
    }

    if (isDev && !forceEnable) {
      console.log("PostHog disabled in development environment");
      return;
    }

    try {
      posthog.init(apiKey, {
        api_host: apiHost,
        // Cross-domain tracking - CRITICAL for maintaining sessions across repositories
        cross_subdomain_cookie: true,
        // Respect user privacy
        respect_dnt: true,
        //------------------- we had disabled some features for cost cutting posthog - commenting it out so that we get full features

        // // --- Event-volume controls (PostHog event budget) ---
        // // Disable high-volume auto-captured events we don't analyse. Explicit
        // // product events and manual $pageview capture are unaffected.
        // autocapture: false, // $autocapture (DOM clicks/changes/submits)
        // capture_pageleave: false, // $pageleave
        // capture_dead_clicks: false, // $dead_click
        // rageclick: false, // $rageclick
        // capture_performance: false, // $web_vitals / network timing
        //---------------------

        // Custom properties
        // property_blacklist: ["$current_url", "$host", "$pathname"], // Avoid duplicating with existing analytics
      });

      this.isInitialized = true;
      this.isEnabled = true;
      this.captureFirstTouch();
      console.log("PostHog initialized successfully");
    } catch (error) {
      console.error("Failed to initialize PostHog:", error);
    }
  }

  // Capture first-touch acquisition source as super + $set_once person properties.
  // The authoritative first touch is the `.codedesign.ai`-scoped `cd_ft` cookie set
  // SYNCHRONOUSLY on the codedesign.ai landing (before PostHog or any interaction), so the
  // real Google/ad referrer survives the hop into the app. We only self-capture from the
  // in-app URL as a fallback when no cookie exists (direct-to-app entry). First touch wins;
  // we never overwrite an existing cd_ft. Never throws.
  captureFirstTouch() {
    try {
      if (typeof window === "undefined") return;
      if (document.cookie.indexOf("cd_ft=") === -1) {
        const qs = new URLSearchParams(window.location.search || "");
        const ref = document.referrer || "";
        let refDomain = "";
        try { refDomain = ref ? new URL(ref).hostname : ""; } catch (e) {}
        const internal = !!refDomain && /(^|\.)codedesign\.(ai|app)$/i.test(refDomain);
        const hasCampaign =
          qs.get("utm_source") || qs.get("utm_campaign") || qs.get("gclid") ||
          qs.get("fbclid") || qs.get("ttclid") || qs.get("gad_source");
        if (!(internal && !hasCampaign)) {
          const ft = {
            utm_source: qs.get("utm_source") || "",
            utm_medium: qs.get("utm_medium") || "",
            utm_campaign: qs.get("utm_campaign") || "",
            utm_term: qs.get("utm_term") || "",
            utm_content: qs.get("utm_content") || "",
            gclid: qs.get("gclid") || "",
            fbclid: qs.get("fbclid") || "",
            ttclid: qs.get("ttclid") || "",
            gad_source: qs.get("gad_source") || "",
            referrer: ref || "$direct",
            referring_domain: internal ? "" : (refDomain || "$direct"),
            landing_host: window.location.hostname,
            landing_path: window.location.pathname,
            ts: new Date().toISOString(),
          };
          document.cookie =
            "cd_ft=" + encodeURIComponent(JSON.stringify(ft)) +
            "; path=/; domain=.codedesign.ai; max-age=31536000; Secure; SameSite=Lax";
        }
      }
      const props = this.getFirstTouch();
      if (props) {
        posthog.register_once(props);
        posthog.setPersonProperties({}, props); // 2nd arg = $set_once
      }
    } catch (e) {
      // attribution must never break analytics
    }
  }

  // Parse the cd_ft cookie into PostHog person-property names. Returns null if absent.
  getFirstTouch() {
    try {
      if (typeof document === "undefined") return null;
      const m = document.cookie.match(/(?:^|; )cd_ft=([^;]+)/);
      if (!m) return null;
      const ft = JSON.parse(decodeURIComponent(m[1]));
      const map = {
        initial_utm_source: ft.utm_source,
        initial_utm_medium: ft.utm_medium,
        initial_utm_campaign: ft.utm_campaign,
        initial_utm_term: ft.utm_term,
        initial_utm_content: ft.utm_content,
        initial_gclid: ft.gclid,
        initial_fbclid: ft.fbclid,
        initial_ttclid: ft.ttclid,
        initial_gad_source: ft.gad_source,
        initial_referrer: ft.referrer,
        initial_referring_domain: ft.referring_domain,
        initial_landing_path: ft.landing_path,
      };
      const out = {};
      Object.keys(map).forEach((k) => { if (map[k]) out[k] = map[k]; });
      return Object.keys(out).length ? out : null;
    } catch (e) {
      return null;
    }
  }

  // Track events
  track(eventName, properties = {}) {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      // Add prefix to distinguish from existing analytics events
      const prefixedEventName = `${eventName}`;
      posthog.capture(prefixedEventName, {
        ...properties,
        source: "codedesign-v2", // Unique identifier for this repository
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("PostHog track error:", error);
    }
  }

  // Identify users
  identify(userId, properties = {}) {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      // 3rd arg = $set_once: first-touch acquisition source sticks to the identified
      // person and is never overwritten by later sessions.
      posthog.identify(
        userId,
        {
          ...properties,
          source: "codedesign-v2", // Unique identifier for this repository
        },
        this.getFirstTouch() || undefined,
      );
    } catch (error) {
      console.error("PostHog identify error:", error);
    }
  }

  // Update user properties (for updating after initial identification)
  setUserProperties(properties = {}) {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      posthog.setPersonProperties({
        ...properties,
        source: "codedesign-v2",
      });
    } catch (error) {
      console.error("PostHog setUserProperties error:", error);
    }
  }

  // Track page views
  page(properties = {}) {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      posthog.capture("$pageview", {
        ...properties,
        source: "codedesign-v2", // Unique identifier for this repository
      });
    } catch (error) {
      console.error("PostHog page error:", error);
    }
  }

  // Get current user info
  getUser() {
    if (!this.isEnabled || !this.isInitialized) return null;

    try {
      return {
        userId: posthog.get_distinct_id(),
        properties: posthog.get_property("$set") || {},
      };
    } catch (error) {
      console.error("PostHog getUser error:", error);
      return null;
    }
  }

  // Reset user (logout)
  reset() {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      posthog.reset();
    } catch (error) {
      console.error("PostHog reset error:", error);
    }
  }

  // Enable/disable tracking
  setEnabled(enabled) {
    this.isEnabled = enabled;
    if (this.isInitialized) {
      if (enabled) {
        posthog.opt_in_capturing();
      } else {
        posthog.opt_out_capturing();
      }
    }
  }

  // Check if PostHog is available
  isAvailable() {
    return this.isEnabled && this.isInitialized;
  }
}

// Create singleton instance
const posthogAnalytics = new PostHogAnalytics();

/**
 * Determines if a user should be considered "paid" based on their workspaces
 * A user is paid if they have at least one workspace with active plans
 * @param {Array} workspaces - Array of workspace objects
 * @returns {boolean} - True if user has any paid workspace
 */
export function getUserPaidStatus(workspaces) {
  if (!Array.isArray(workspaces) || workspaces.length === 0) {
    return false;
  }

  return workspaces.some((workspace) => {
    const activePlans = workspace?.activePlans || [];
    return activePlans.length > 0;
  });
}

/**
 * Calculates detailed workspace statistics for PostHog tracking
 * @param {Array} workspaces - Array of workspace objects
 * @returns {Object} - Statistics about workspaces and paid status
 */
export function getWorkspaceStats(workspaces) {
  if (!Array.isArray(workspaces) || workspaces.length === 0) {
    return {
      is_paid_user: false,
      workspace_count: 0,
      paid_workspace_count: 0,
      total_active_plans: 0,
    };
  }

  const paidWorkspaces = workspaces.filter((workspace) => {
    const activePlans = workspace?.activePlans || [];
    return activePlans.length > 0;
  });

  const totalActivePlans = workspaces.reduce((sum, workspace) => {
    const activePlans = workspace?.activePlans || [];
    return sum + activePlans.length;
  }, 0);

  return {
    is_paid_user: paidWorkspaces.length > 0,
    workspace_count: workspaces.length,
    paid_workspace_count: paidWorkspaces.length,
    total_active_plans: totalActivePlans,
  };
}

export default posthogAnalytics;
